import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Button,
  IconButton,
  TextField,
  Paper,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import ORGANIZATION_TYPES from 'shared/constants/organizationTypes';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Language as LanguageIcon,
  People as PeopleIcon,
  AccountBalance as DepartmentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox as MuiCheckbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import BackButton from 'components/common/buttons/BackButton';
import EmailDomainsManager from './EmailDomainsManager';
import { useUser } from 'shared/helpers/axios';
import InfrastructureEditor from './InfrastructureEditor';
import ReportButton from 'components/common/ReportButton';
import VerifyMembershipDialog from 'components/social/profile/VerifyMembershipDialog';


const OrganizationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useUser();
  const isNewOrg = id === 'new';

  // Fetch organization data
  const { data: org, error, isLoading, mutate } = useSWR(
    !isNewOrg ? `/consortium/organizations/${id}/` : null,
    fetcher
  );

  // Fetch countries
  const { data: countries = [] } = useSWR('/consortium/countries/', fetcher);

  // State
  const [editing, setEditing] = useState(isNewOrg);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });


  // Form data
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    abbreviation: '',
    org_type: 'other',
    website: '',
    description: '',
    logo: null
  });

  // Department states
  const [departments, setDepartments] = useState([]);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptDesc, setNewDeptDesc] = useState('');

  // Infrastructure states
  const [infrastructureItems, setInfrastructureItems] = useState([]);
  const [newInfraName, setNewInfraName] = useState('');
  const [newInfraDesc, setNewInfraDesc] = useState('');
  const [newInfraCategory, setNewInfraCategory] = useState('');

  // Address states
  const [addresses, setAddresses] = useState([]);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const userCountryCode = currentUser?.user?.country?.code || 'USA';
  const [addressForm, setAddressForm] = useState({
    line1: '',
    line2: '',
    city: '',
    state_region: '',
    postal_code: '',
    country_code: userCountryCode,
    type: 'business',
    is_primary: false
  });

  // Complaint/Report states
  const [complaintDialogOpen, setComplaintDialogOpen] = useState(false);
  const [complaintForm, setComplaintForm] = useState({
    reported_user_id: '',
    reported_user_name: '',
    reason: '',
    description: ''
  });
  const [editors, setEditors] = useState([]);
  const [claimingEditorRights, setClaimingEditorRights] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);

  // Check if user can edit this organization
  const canEditOrg = React.useMemo(() => {
    if (isNewOrg) return true;
    if (!org || !currentUser) return false;
    
    // Superusers can always edit
    if (currentUser?.user?.is_superuser) return true;
    
    // Check the org's own user_membership (always fresh from the org endpoint)
    if (org?.user_membership?.can_edit_org) return true;

    // Fallback: check user's cached org_memberships list
    const membership = currentUser?.user?.org_memberships?.find(m => m?.org_id === org.id);
    if (!membership) return false;
    
    return membership.can_edit_org;
  }, [org, currentUser, isNewOrg]);

  const canDeleteOrg = canEditOrg && (currentUser?.user?.is_superuser || org?.user_membership?.is_verified); 


  // Initialize form when org loads
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (org && !isNewOrg) {
      setFormData({
        name: org.name || '',
        short_name: org.short_name || '',
        abbreviation: org.abbreviation || '',
        org_type: org.org_type || 'other',
        website: org.website || '',
        description: org.description || '',
        logo: null
      });
      setDepartments(org.departments_detail || []);
      setInfrastructureItems(org.infrastructure || []);
      setAddresses(org.addresses_detail || []);
      
      // Fetch editors list
      fetchEditors();
    }
  }, [org, isNewOrg]);

  const fetchEditors = async () => {
    if (!id || isNewOrg) return;
    try {
      const response = await axiosService.get(`/consortium/organizations/${id}/editors/`);
      setEditors(response.data || []);
    } catch (e) {
      console.error('Failed to fetch editors:', e);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setUpdating(true);
    
      if (isNewOrg) {
        // Create new organization
        const response = await axiosService.post('/consortium/organizations/', formData);
        // Refresh user data so org_memberships includes the auto-created editor membership
        currentUser.refreshUserData();
        setSnackbar({ open: true, message: 'Organization created successfully', severity: 'success' });
        navigate(`/organization/${response.data.id}/`);
      } else {
        // Update existing organization
       
        await axiosService.patch(`/consortium/organizations/${id}/`, formData);
        setSnackbar({ open: true, message: 'Organization updated successfully', severity: 'success' });
        mutate();
        setEditing(false);
      }
    } catch (e) {
      console.error('Failed to save organization', e);
      setSnackbar({ open: true, message: 'Failed to save organization', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDeptName.trim()) {
      setSnackbar({ open: true, message: 'Department name is required', severity: 'warning' });
      return;
    }

    if (isNewOrg || !id) {
      setSnackbar({ open: true, message: 'Please save the organization first before adding departments', severity: 'warning' });
      return;
    }

    try {
      setUpdating(true);
      const deptData = {
        org: id,
        name: newDeptName,
        description: newDeptDesc
      };
      const response = await axiosService.post(`/consortium/organizations/${id}/departments/`, deptData);
      setDepartments([...departments, response.data]);
      setNewDeptName('');
      setNewDeptDesc('');
      setSnackbar({ open: true, message: 'Department added successfully', severity: 'success' });
      mutate(); // Refresh organization data
    } catch (e) {
      console.error('Failed to add department:', e);
      const errorMsg = e?.response?.data?.detail || e?.response?.data?.name?.[0] || 'Failed to add department';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleClaimEditorRights = async () => {
    if (!window.confirm('Claim editor rights for this organization? Other members can report abuse if you misuse these permissions.')) return;
    
    try {
      setClaimingEditorRights(true);
      await axiosService.post(`/consortium/organizations/${id}/claim-editor-rights/`);
      setSnackbar({ open: true, message: 'Editor rights granted successfully', severity: 'success' });
      mutate();
      fetchEditors();
    } catch (e) {
      console.error('Failed to claim editor rights:', e);
      setSnackbar({ open: true, message: 'Failed to claim editor rights', severity: 'error' });
    } finally {
      setClaimingEditorRights(false);
    }
  };

  const handleOpenComplaintDialog = (editor) => {
    setComplaintForm({
      reported_user_id: editor.user_id,
      reported_user_name: editor.user_name,
      reason: '',
      description: ''
    });
    setComplaintDialogOpen(true);
  };

  const handleCloseComplaintDialog = () => {
    setComplaintDialogOpen(false);
    setComplaintForm({
      reported_user_id: '',
      reported_user_name: '',
      reason: '',
      description: ''
    });
  };

  const handleSubmitComplaint = async () => {
    if (!complaintForm.reason || !complaintForm.description) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'warning' });
      return;
    }

    try {
      setUpdating(true);
      await axiosService.post(`/consortium/organizations/${id}/report-editor/`, {
        reported_user_id: complaintForm.reported_user_id,
        reason: complaintForm.reason,
        description: complaintForm.description
      });
      setSnackbar({ open: true, message: 'Complaint submitted successfully. Admins will review it.', severity: 'success' });
      handleCloseComplaintDialog();
    } catch (e) {
      console.error('Failed to submit complaint:', e);
      setSnackbar({ open: true, message: 'Failed to submit complaint', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleAddInfrastructure = async () => {
    if (!newInfraName.trim()) return;

    try {
      setUpdating(true);
      const newItem = {
        name: newInfraName,
        description: newInfraDesc,
        category: newInfraCategory,
        added_by: currentUser.user?.username || currentUser.user?.email || ''
      };
      
      await axiosService.post(`/consortium/organizations/${id}/add-infrastructure/`, newItem);
      setInfrastructureItems([...infrastructureItems, newItem]);
      setNewInfraName('');
      setNewInfraDesc('');
      setNewInfraCategory('');
      setSnackbar({ open: true, message: 'Infrastructure added', severity: 'success' });
      mutate();
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to add infrastructure', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveInfrastructure = async (index) => {
    try {
      setUpdating(true);
      await axiosService.delete(`/consortium/organizations/${id}/remove-infrastructure/${index}/`);
      setInfrastructureItems(infrastructureItems.filter((_, i) => i !== index));
      setSnackbar({ open: true, message: 'Infrastructure removed', severity: 'success' });
      mutate();
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to remove infrastructure', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenAddressDialog = (address = null) => {
    if (address) {
      setEditingAddressId(address.id);
      setAddressForm({
        line1: address.line1 || '',
        line2: address.line2 || '',
        city: address.city || '',
        state_region: address.state_region || '',
        postal_code: address.postal_code || '',
        country_code: address.country?.code || address.country_code || 'USA',
        type: address.type || 'business',
        is_primary: address.is_primary || false
      });
    } else {
      setEditingAddressId(null);
      setAddressForm({
        line1: '',
        line2: '',
        city: '',
        state_region: '',
        postal_code: '',
        country_code: userCountryCode,
        type: 'business',
        is_primary: false
      });
    }
    setAddressDialogOpen(true);
  };

  const handleCloseAddressDialog = () => {
    setAddressDialogOpen(false);
    setEditingAddressId(null);
  };

  const handleSaveAddress = async () => {
    try {
      setUpdating(true);
      
      if (editingAddressId) {
        // Update existing address
        await axiosService.put(`/consortium/organizations/${id}/update-address/${editingAddressId}/`, addressForm);
        setSnackbar({ open: true, message: 'Address updated successfully', severity: 'success' });
      } else {
        // Add new address
        await axiosService.post(`/consortium/organizations/${id}/add-address/`, addressForm);
        setSnackbar({ open: true, message: 'Address added successfully', severity: 'success' });
      }
      
      mutate();
      handleCloseAddressDialog();
    } catch (e) {
      console.error('Failed to save address:', e);
      setSnackbar({ open: true, message: 'Failed to save address', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      setUpdating(true);
      await axiosService.delete(`/consortium/organizations/${id}/remove-address/${addressId}/`);
      setSnackbar({ open: true, message: 'Address deleted successfully', severity: 'success' });
      mutate();
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete address', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteOrganization = async () => {
    try {
      setDeleting(true);
      await axiosService.delete(`/consortium/organizations/${id}/`);
      setSnackbar({ open: true, message: 'Organization deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      // Navigate to collaboration hub after short delay
      setTimeout(() => {
        navigate('/hub/');
      }, 1500);
    } catch (e) {
      console.error('Failed to delete organization:', e);
      const errorMsg = e.response?.data?.error || e.response?.data?.detail || 'Failed to delete organization';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (isLoading && !isNewOrg) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !isNewOrg) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Failed to load organization</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2} >
             <BackButton position="inline" />
            <Typography variant="h4" fontWeight={800} sx={{  letterSpacing: 0.2 }}>
              Organization
            </Typography>
          </Box>



      <Grid container spacing={3}>


        {/* Left Column */}
        <Grid item xs={12} md={8}>



        {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }} alt={org?.name || 'Organization'}>
              <BusinessIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Box>
              {editing ? (
                <>

              <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Organization Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    fullWidth
                    required
                    sx={{ mb: 1.5 }}
                  />

                  <TextField
                      label="Short Name"
                      value={formData.short_name}
                      onChange={(e) => handleInputChange('short_name', e.target.value)}
                      // size="small"
                      required
                      placeholder="e.g., MIT"
                      
                    />
            
            
                    {/* <TextField
                      label="Abbreviation"
                      value={formData.abbreviation}
                      onChange={(e) => handleInputChange('abbreviation', e.target.value)}
                      size="small"
                      placeholder="e.g., MASS INST TECH"
                    /> */}
              </Box>
                </>
              ) : (
                <Typography variant="h4" fontWeight={600}>
                  {org?.name || 'New Organization'} {org?.short_name && `(${org.short_name})`}
                </Typography>
              )}
              

              {editing ? (
                <Autocomplete
                  freeSolo
                  options={ORGANIZATION_TYPES}
                  value={formData.org_type}
                  onChange={(_, newValue) => {
                    handleInputChange('org_type', newValue || 'Other');
                  }}
                  onInputChange={(_, newInputValue) => {
                    handleInputChange('org_type', newInputValue || 'Other');
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organization Type"
                      placeholder="Select or type"
                      size="small"
                    />
                  )}
                />
              ) : (
                <Chip label={org?.org_type ? org.org_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Other'} size="small" sx={{ mt: 1 }} />
              )}



{/* Website */}
        {editing ? (
          <TextField
            label="Website"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            fullWidth
            placeholder="https://example.com"
            InputProps={{
              startAdornment: <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ mt: 1 }}
          />
        ) : org?.website && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <LanguageIcon color="action" />
            <Typography
              component="a"
              href={org.website}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {org.website}
            </Typography>
          </Box>
        )}

            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {editing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    if (isNewOrg) {
                      navigate('/hub/');
                    } else {
                      setEditing(false);
                      setFormData({
                        name: org.name,
                        short_name: org.short_name,
                        abbreviation: org.abbreviation || '',
                        org_type: org.org_type,
                        website: org.website,
                        description: org.description,
                        logo: null
                      });
                    }
                  }}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={updating || !formData.name}
                >
                  {isNewOrg ? 'Create' : 'Save'}
                </Button>
              </>
            ) : 
              canEditOrg &&  (
              <>

              { canDeleteOrg && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  title={!canDeleteOrg ? 'You need to be a verified member with delete permissions' : 'Delete organization'}
                >
                  Delete
                </Button>
              )}

                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setEditing(true)}
                  disabled={!canEditOrg}
                  title={!canEditOrg ? 'You need to be a verified member with edit permissions' : 'Edit organization'}
                >
                  Edit
                </Button>
              </>
            )}
            <ReportButton
              targetType="organization"
              targetId={org?.id}
              targetLabel={org?.name}
              size="small"
            />
          </Box>






        </Box>

 






        {/* <Typography variant="h6" gutterBottom mt={2}>
          Description
        </Typography> */}
              {editing ? (
                <TextField
                  multiline
                  rows={6}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  fullWidth
                  placeholder="Enter organization description..."
                />
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }} mt={2}>
                  {org?.description || 'No description provided.'}
                </Typography>
              )}  

      </Paper>



          
          {/* Description */}
          {/* <Card sx={{ mb: 3 }}>
            <CardContent>
      
            </CardContent>
          </Card> */}




{/* Verify Membership Notice */}
          {!isNewOrg && org?.user_membership && !org.user_membership.is_verified && (
            <Alert severity="warning" sx={{ mb: 3 }} action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => setVerifyDialogOpen(true)}
              >
                Verify Now
              </Button>
            }>
              Your affiliation with this organization is not verified yet. Verify to unlock full membership features.
            </Alert>
          )}
          {/* Claim Editor Rights Notice */}
          {!isNewOrg && !canEditOrg && org?.user_membership?.is_verified && (
            <Alert severity="info" sx={{ mb: 3 }} action={
              <Button 
                color="inherit" 
                size="small"
                onClick={handleClaimEditorRights}
                disabled={claimingEditorRights}
              >
                Claim Editor Rights
              </Button>
            }>
              You are a member of this organization. Click to claim editor rights and help manage organization information.
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Note: Other members can report abuse if these permissions are misused.
              </Typography>
            </Alert>
          )}






          {/* Departments */}
          {!isNewOrg && (canEditOrg || departments.length > 0) && (
            <Card sx={{ my: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    <DepartmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Departments {departments.length > 0 && `(${departments.length})`}
                  </Typography>
                  {canEditOrg && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setNewDeptName(' ')}
                    >
                      Add Department
                    </Button>
                  )}
                </Box>
                
                {departments.length > 0 ? (
                  <List>
                    {departments.map((dept, index) => (
                      <ListItem 
                        key={index} 
                        divider
                        button
                        onClick={() => navigate(`/organization/${id}/department/${dept.id}/`)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <ListItemText
                          primary={dept.name}
                          secondary={dept.description.slice(0, 50) + (dept.description.length > 50 ? '...' : '')}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No departments added yet.
                  </Typography>
                )}

                {canEditOrg && newDeptName &&  (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={2}>
                      <TextField
                        label="Department Name"
                        value={newDeptName}
                        onChange={(e) => setNewDeptName(e.target.value)}
                        size="small"
                        fullWidth
                        autoFocus
                      />
                      <TextField
                        label="Description"
                        value={newDeptDesc}
                        onChange={(e) => setNewDeptDesc(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleAddDepartment}
                          disabled={!newDeptName.trim() || updating}
                        >
                          Save Department
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setNewDeptName('');
                            setNewDeptDesc('');
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Infrastructure */}
          {/* {!isNewOrg && (canEditOrg || infrastructureItems.length > 0) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Infrastructure {infrastructureItems.length > 0 && `(${infrastructureItems.length})`}
                  </Typography>
                  {canEditOrg && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setNewInfraName(' ')}
                    >
                      Add Infrastructure
                    </Button>
                  )}
                </Box>
                
                {infrastructureItems.length > 0 ? (
                  <Stack spacing={2} sx={{ mb: 2 }}>
                    {infrastructureItems.map((item, index) => (
                      <Paper key={index} sx={{ p: 2, position: 'relative' }}>
                        {canEditOrg && (
                          <IconButton
                            size="small"
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                            onClick={() => handleRemoveInfrastructure(index)}
                            disabled={updating}
                            title="Remove infrastructure"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                        <Typography variant="subtitle2" fontWeight={600}>{item.name}</Typography>
                        {item.category && (
                          <Typography variant="caption" color="text.secondary">
                            Category: {item.category}
                          </Typography>
                        )}
                        {item.description && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {item.description}
                          </Typography>
                        )}
                        {item.added_by && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Added by: {item.added_by}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No infrastructure added yet.
                  </Typography>
                )}

                {canEditOrg && newInfraName  && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={2}>
                      <TextField
                        label="Infrastructure Name"
                        value={newInfraName}
                        onChange={(e) => setNewInfraName(e.target.value)}
                        size="small"
                        fullWidth
                        autoFocus
                      />
                      <TextField
                        label="Description"
                        value={newInfraDesc}
                        onChange={(e) => setNewInfraDesc(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                      />
                      <TextField
                        label="Category"
                        value={newInfraCategory}
                        onChange={(e) => setNewInfraCategory(e.target.value)}
                        size="small"
                        fullWidth
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleAddInfrastructure}
                          disabled={!newInfraName.trim() || updating}
                        >
                          Save Infrastructure
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setNewInfraName('');
                            setNewInfraDesc('');
                            setNewInfraCategory('');
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          )} */}


  

<InfrastructureEditor
  infrastructureItems={infrastructureItems}
  setInfrastructureItems={setInfrastructureItems}
  canEdit={canEditOrg}
  addUrl={`/consortium/organizations/${id}/add-infrastructure/`}
  removeUrl={`/consortium/organizations/${id}/remove-infrastructure/`}
  mutate={mutate}
  setSnackbar={setSnackbar}
/>

       

{/* <Accordion 

  sx={{ 
    mb: 3,
    '&:before': { display: 'none' },
    boxShadow: 1,
    borderRadius: 1,
    overflow: 'hidden'
  }}
  defaultExpanded={false}
>
  <AccordionSummary
    // expandIcon={<ExpandMoreIcon />}
    aria-controls="panel-content"
    id="panel-header"
    sx={{
      backgroundColor: 'background.paper',
      borderBottom: '1px solid',
      borderColor: 'divider',
      minHeight: 56,
      '&.Mui-expanded': { minHeight: 56 },
    }}
  >
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      width: '100%'
    }}>
      <Typography variant="h6">
        Infrastructure {infrastructureItems.length > 0 && `(${infrastructureItems.length})`}
      </Typography>
      {canEditOrg && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={(e) => {
            e.stopPropagation(); // Prevent accordion toggle
            setNewInfraName(' ');
          }}
        >
          Add Infrastructure
        </Button>
      )}
    </Box>
  </AccordionSummary>
  
  <AccordionDetails sx={{ pt: 2, pb: 3 }}>
    {/* Rest of the content remains here 
    {infrastructureItems.length > 0 ? (
      <Stack spacing={2} sx={{ mb: 2 }}>
        {infrastructureItems.map((item, index) => (
          <Paper key={index} sx={{ p: 2, position: 'relative' }}>
            {canEditOrg && (
              <IconButton
                size="small"
                sx={{ position: 'absolute', top: 8, right: 8 }}
                onClick={() => handleRemoveInfrastructure(index)}
                disabled={updating}
                title="Remove infrastructure"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            <Typography variant="subtitle2" fontWeight={600}>{item.name}</Typography>
            {item.category && (
              <Typography variant="caption" color="text.secondary">
                Category: {item.category}
              </Typography>
            )}
            {item.description && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {item.description}
              </Typography>
            )}
            {item.added_by && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Added by: {item.added_by}
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        No infrastructure added yet.
      </Typography>
    )}

    {newInfraName && (
      <>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={2}>
          <TextField
            label="Infrastructure Name"
            value={newInfraName}
            onChange={(e) => setNewInfraName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          />
          <TextField
            label="Description"
            value={newInfraDesc}
            onChange={(e) => setNewInfraDesc(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Category"
            value={newInfraCategory}
            onChange={(e) => setNewInfraCategory(e.target.value)}
            size="small"
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleAddInfrastructure}
              disabled={!newInfraName.trim() || updating}
            >
              Save Infrastructure
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setNewInfraName('');
                setNewInfraDesc('');
                setNewInfraCategory('');
              }}
            >
              Cancel
            </Button>
          </Box>
        </Stack>
      </>
    )}
  </AccordionDetails>
</Accordion> 
  )}*/}



      
        </Grid>


        {/* Right Column - Stats & Info */}
        <Grid item xs={12} md={4}>
          
        {/* Addresses */}
          {!isNewOrg && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    <LocationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Addresses
                  </Typography>


                  {canEditOrg && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenAddressDialog()}
                  >
                    Add Address
                  </Button>
                  )}

                </Box>

                {addresses.length === 0 ? (
                  <Alert severity="info">No addresses added yet.</Alert>
                ) : (
                  <Stack spacing={2}>
                    {addresses.map((addr) => (
                      <Paper key={addr.id} sx={{ p: 2, position: 'relative' }}>
                      
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {addr.line1}
                            </Typography>
                            {addr.line2 && (
                              <Typography variant="body2">{addr.line2}</Typography>
                            )}
                            <Typography variant="body2">
                              {[addr.city, addr.state_region, addr.postal_code].filter(Boolean).join(', ')}
                            </Typography>
                            <Typography variant="body2">
                              {addr.country?.name || addr.country_code || 'N/A'}
                            </Typography>
                          
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="center">

                          {/* <Chip label={addr.type} size="small" /> */}

                          {addr.is_primary && (
                          <Chip
                            label="Primary"
                            size="small"
                            color="primary"
                            // sx={{ position: 'absolute', top: 12, right: 58 }}
                          />
                        )}


{canEditOrg && (
  <>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenAddressDialog(addr)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAddress(addr.id)}
                              disabled={updating}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>

  </>
)}
                          </Stack>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}
              
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Statistics</Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Members</Typography>
                  <Typography variant="h5">{org?.memberships_count || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Departments</Typography>
                  <Typography variant="h5">{departments.length}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Infrastructure Items</Typography>
                  <Typography variant="h5">{infrastructureItems.length}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Current Editors Card */}
          {!isNewOrg && editors.length > 0 && (
            <Card sx={{ my: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <PeopleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Current Editors
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Members with edit permissions for this organization
                </Typography>
                
                <List>
                  {editors.map((editor) => (
                    <ListItem 
                      key={editor.user_id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1
                      }}
                      secondaryAction={
                        org?.user_membership && editor.user_id !== currentUser?.id && (
                          <Button
                            size="small"
                            color="warning"
                            onClick={() => handleOpenComplaintDialog(editor)}
                          >
                            Report
                          </Button>
                        )
                      }
                    >
                      <ListItemText
                        primary={editor.user_name}
                        secondary={
                          <>
                            {editor.department_name && (
                              <Chip 
                                label={editor.department_name} 
                                size="small" 
                                sx={{ mr: 1, mt: 0.5 }}
                              />
                            )}
                            {editor.role && (
                              <Chip 
                                label={editor.role} 
                                size="small" 
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>

                <Alert severity="info" sx={{ mt: 2 }}>
                  If you notice any editor misusing their permissions or providing false information, 
                  please use the Report button to submit a complaint.
                </Alert>
              </CardContent>
            </Card>
          )}

          {/* Email Domains Manager - Only for non-new organizations */}
          {!isNewOrg && canEditOrg && (
            <Box sx={{ mt: 3 }}>
              <EmailDomainsManager
                organizationId={id}
                organization={org}
                canEdit={canEditOrg}
                onUpdate={mutate}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Complaint Dialog */}
      <Dialog open={complaintDialogOpen} onClose={handleCloseComplaintDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Report Editor Abuse
          <IconButton
            onClick={handleCloseComplaintDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are reporting <strong>{complaintForm.reported_user_name}</strong> for misusing editor permissions.
          </Alert>
          
          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Reason</InputLabel>
              <Select
                value={complaintForm.reason}
                onChange={(e) => setComplaintForm({ ...complaintForm, reason: e.target.value })}
                label="Reason"
              >
                <MenuItem value="false_information">Providing False Information</MenuItem>
                <MenuItem value="unauthorized_changes">Unauthorized Changes</MenuItem>
                <MenuItem value="data_manipulation">Data Manipulation</MenuItem>
                <MenuItem value="fraud">Fraudulent Activity</MenuItem>
                <MenuItem value="inappropriate_behavior">Inappropriate Behavior</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description"
              value={complaintForm.description}
              onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })}
              multiline
              rows={4}
              fullWidth
              required
              placeholder="Please provide detailed information about the issue..."
              helperText="Be specific about what actions concern you and when they occurred"
            />
          </Stack>

          <Alert severity="info" sx={{ mt: 2 }}>
            Your complaint will be reviewed by system administrators. False reports may result in consequences for your account.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseComplaintDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitComplaint}
            variant="contained"
            color="warning"
            disabled={updating || !complaintForm.reason || !complaintForm.description}
          >
            Submit Complaint
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Dialog */}
      <Dialog open={addressDialogOpen} onClose={handleCloseAddressDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAddressId ? 'Edit Address' : 'Add Address'}
          <IconButton
            onClick={handleCloseAddressDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Address Line 1"
              value={addressForm.line1}
              onChange={(e) => setAddressForm({ ...addressForm, line1: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Address Line 2"
              value={addressForm.line2}
              onChange={(e) => setAddressForm({ ...addressForm, line2: e.target.value })}
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  value={addressForm.city}
                  onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="State/Region"
                  value={addressForm.state_region}
                  onChange={(e) => setAddressForm({ ...addressForm, state_region: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Postal Code"
                  value={addressForm.postal_code}
                  onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={countries}
                  getOptionLabel={(option) => `${option.name} (${option.code})`}
                  value={countries.find(c => c.code === addressForm.country_code) || null}
                  onChange={(e, newValue) => {
                    setAddressForm({ ...addressForm, country_code: newValue?.code || 'USA' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      required
                      placeholder="Search countries..."
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  fullWidth
                />
              </Grid>
            </Grid>
            <FormControl fullWidth>
              <InputLabel>Address Type</InputLabel>
              <Select
                value={addressForm.type}
                onChange={(e) => setAddressForm({ ...addressForm, type: e.target.value })}
                label="Address Type"
              >
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="residential">Residential</MenuItem>
                <MenuItem value="mailing">Mailing</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <MuiCheckbox
                  checked={addressForm.is_primary}
                  onChange={(e) => setAddressForm({ ...addressForm, is_primary: e.target.checked })}
                />
              }
              label="Set as primary address"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddressDialog}>Cancel</Button>
          <Button
            onClick={handleSaveAddress}
            variant="contained"
            disabled={updating || !addressForm.line1 || !addressForm.city}
          >
            {editingAddressId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{org?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will permanently delete the organization and all associated data including:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>All departments</li>
            <li>All addresses</li>
            <li>All infrastructure items</li>
            <li>All memberships</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteOrganization}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Organization'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify Membership Dialog */}
      <VerifyMembershipDialog
        open={verifyDialogOpen}
        onClose={() => { setVerifyDialogOpen(false); mutate(); }}
        membership={org?.user_membership ? {
          membership_id: org.user_membership.id,
          organization_name: org.name,
        } : null}
        onVerified={() => mutate()}
        onUploadDocument={async (membershipId, doc) => {
          const formData = new FormData();
          formData.append('verification_document', doc);
          await axiosService.patch(
            `/consortium/organization-memberships/${membershipId}/`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          mutate();
        }}
      />
    </Container>
  );
};

export default OrganizationPage;
